import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClipboardPaste, Link2, Upload, History, Plus } from "lucide-react";
import { toast } from "sonner";
import PasteIngestionTab from "./PasteIngestionTab";
import LinkIngestionTab from "./LinkIngestionTab";
import UploadIngestionTab from "./UploadIngestionTab";
import IngestionDraftPreview from "./IngestionDraftPreview";
import IngestionHistoryList from "./IngestionHistoryList";
import { firecrawlApi } from "@/lib/api/firecrawl";
import { useSaveIngestion, useUpdateIngestion, useDeleteIngestion, useLogScrapeEvent, useExtractIngestion, useIngestion } from "@/hooks/useIngestions";
import { useAuth } from "@/hooks/useAuth";
import { ingestionToDraft, computeContentHash } from "./types";
import type { IngestionDraft, RawIngestion, ReviewStatus } from "./types";

type ViewMode = "new" | "history";

const ActivityIngestionPanel = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("new");
  const [draft, setDraft] = useState<IngestionDraft | null>(null);
  const [savedIngestionId, setSavedIngestionId] = useState<string | null>(null);
  const [reviewStatus, setReviewStatus] = useState<ReviewStatus | null>(null);
  
  const { user } = useAuth();
  const saveIngestion = useSaveIngestion();
  const updateIngestion = useUpdateIngestion();
  const deleteIngestion = useDeleteIngestion();
  const logScrapeEvent = useLogScrapeEvent();
  const extractIngestion = useExtractIngestion();
  
  // Fetch full ingestion data when we have a saved ID (to get extraction results)
  const { data: currentIngestion, refetch: refetchIngestion } = useIngestion(savedIngestionId);

  const handleCapture = (newDraft: IngestionDraft) => {
    setDraft(newDraft);
    setSavedIngestionId(null);
    setReviewStatus(null);
  };

  const handleClear = () => {
    setDraft(null);
    setSavedIngestionId(null);
    setReviewStatus(null);
  };

  const handleUpdateDraft = (updates: Partial<IngestionDraft>) => {
    setDraft((prev) => (prev ? { ...prev, ...updates } : null));
    
    // If this is a saved ingestion, also update in database
    if (savedIngestionId && user) {
      const currentDraft = draft;
      updateIngestion.mutate({
        id: savedIngestionId,
        updates: {
          status: updates.status,
          data_type: updates.data_type,
        },
        userId: user.id,
        previousValues: currentDraft ? {
          status: currentDraft.status,
          data_type: currentDraft.data_type,
        } : undefined,
      });
    }
  };

  const handleReviewStatusChange = async (status: ReviewStatus | null) => {
    if (!savedIngestionId || !user) return;
    
    const previousStatus = reviewStatus;
    setReviewStatus(status);
    
    await updateIngestion.mutateAsync({
      id: savedIngestionId,
      updates: {
        review_status: status,
        reviewed_at: status ? new Date().toISOString() : null,
        reviewed_by: status ? user.id : null,
      },
      userId: user.id,
      previousValues: { review_status: previousStatus },
    });
    
    toast.success(`Review status updated to ${status || 'not reviewed'}`);
  };

  const handleRescrape = async () => {
    if (!draft || draft.source.source_type !== "link" || !draft.source.url) return;

    // Update to processing status
    setDraft((prev) =>
      prev ? { ...prev, status: "processing", scrape_error: undefined } : null
    );

    // Log scrape started event if saved
    if (savedIngestionId && user) {
      logScrapeEvent.mutate({
        ingestionId: savedIngestionId,
        eventType: "scrape_started",
        payload: { url: draft.source.url, scrape_options: draft.scrape_options },
        userId: user.id,
      });
    }

    try {
      const response = await firecrawlApi.scrape(
        draft.source.url,
        draft.scrape_options
      );

      if (response.success && response.data) {
        const newContent = response.data?.markdown || "";
        const newHash = await computeContentHash(newContent);
        
        setDraft((prev) =>
          prev
            ? {
                ...prev,
                raw_content: newContent,
                raw_html: response.data?.html,
                scraped_at: new Date().toISOString(),
                status: "processed",
                scrape_error: undefined,
              }
            : null
        );

        // Update database if saved
        if (savedIngestionId && user) {
          await updateIngestion.mutateAsync({
            id: savedIngestionId,
            updates: {
              raw_content: newContent,
              raw_html: response.data?.html,
              scraped_at: new Date().toISOString(),
              status: "processed",
              scrape_error: null,
              content_hash: newHash,
            },
            userId: user.id,
            previousValues: { status: "processing" },
          });

          logScrapeEvent.mutate({
            ingestionId: savedIngestionId,
            eventType: "scrape_completed",
            payload: { content_hash: newHash, content_length: newContent.length },
            userId: user.id,
          });
        }

        toast.success("Page re-scraped successfully");
      } else {
        const errorMessage = response.error || "Unknown error occurred";
        setDraft((prev) =>
          prev
            ? {
                ...prev,
                status: "failed",
                scrape_error: errorMessage,
              }
            : null
        );

        if (savedIngestionId && user) {
          await updateIngestion.mutateAsync({
            id: savedIngestionId,
            updates: { status: "failed", scrape_error: errorMessage },
            userId: user.id,
            previousValues: { status: "processing" },
          });

          logScrapeEvent.mutate({
            ingestionId: savedIngestionId,
            eventType: "scrape_failed",
            payload: { error: errorMessage, scrape_options: draft.scrape_options },
            userId: user.id,
          });
        }

        toast.error("Failed to re-scrape page", { description: response.error });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to re-scrape";
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              status: "failed",
              scrape_error: errorMessage,
            }
          : null
      );

      if (savedIngestionId && user) {
        logScrapeEvent.mutate({
          ingestionId: savedIngestionId,
          eventType: "scrape_failed",
          payload: { error: errorMessage },
          userId: user.id,
        });
      }

      toast.error("Failed to re-scrape page", { description: errorMessage });
    }
  };

  const handleSave = async () => {
    if (!draft || !user) {
      toast.error("Cannot save: no draft or user");
      return;
    }

    try {
      const saved = await saveIngestion.mutateAsync({
        draft,
        userId: user.id,
      });
      
      setSavedIngestionId(saved.id);
      setReviewStatus(saved.review_status);
      toast.success("Ingestion saved successfully");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to save";
      toast.error("Failed to save ingestion", { description: errorMessage });
    }
  };

  const handleDelete = async () => {
    if (!savedIngestionId) return;

    try {
      await deleteIngestion.mutateAsync(savedIngestionId);
      handleClear();
      toast.success("Ingestion deleted");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to delete";
      toast.error("Failed to delete ingestion", { description: errorMessage });
    }
  };

  const handleSelectFromHistory = (ingestion: RawIngestion) => {
    setDraft(ingestionToDraft(ingestion));
    setSavedIngestionId(ingestion.id);
    setReviewStatus(ingestion.review_status);
    setViewMode("new"); // Switch to edit view
  };

  const handleExtract = async () => {
    if (!savedIngestionId) return;
    
    try {
      await extractIngestion.mutateAsync(savedIngestionId);
      // Refetch to get the updated extraction data
      await refetchIngestion();
      toast.success("Extraction completed successfully");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Extraction failed";
      toast.error("Failed to extract data", { description: errorMessage });
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 px-4 pb-4 overflow-y-auto">
      <p className="text-sm text-white/70 mb-3">
        Ingest after-school activity information from various sources.
      </p>

      {/* View Mode Toggle */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setViewMode("new")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
            viewMode === "new"
              ? "bg-white/20 text-white"
              : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80"
          }`}
        >
          <Plus className="w-3.5 h-3.5" />
          New
        </button>
        <button
          onClick={() => setViewMode("history")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
            viewMode === "history"
              ? "bg-white/20 text-white"
              : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80"
          }`}
        >
          <History className="w-3.5 h-3.5" />
          History
        </button>
      </div>

      {viewMode === "new" ? (
        <>
          <Tabs defaultValue="paste" className="flex-1">
            <TabsList className="bg-white/10 border border-white/20 w-full grid grid-cols-3">
              <TabsTrigger
                value="paste"
                className="text-white/70 data-[state=active]:bg-white/20 data-[state=active]:text-white gap-1.5"
              >
                <ClipboardPaste className="w-3.5 h-3.5" />
                Paste
              </TabsTrigger>
              <TabsTrigger
                value="link"
                className="text-white/70 data-[state=active]:bg-white/20 data-[state=active]:text-white gap-1.5"
              >
                <Link2 className="w-3.5 h-3.5" />
                Link
              </TabsTrigger>
              <TabsTrigger
                value="upload"
                className="text-white/70 data-[state=active]:bg-white/20 data-[state=active]:text-white gap-1.5"
              >
                <Upload className="w-3.5 h-3.5" />
                Upload
              </TabsTrigger>
            </TabsList>

            <TabsContent value="paste" className="mt-3">
              <PasteIngestionTab onCapture={handleCapture} />
            </TabsContent>

            <TabsContent value="link" className="mt-3">
              <LinkIngestionTab onCapture={handleCapture} onUpdateDraft={handleUpdateDraft} />
            </TabsContent>

            <TabsContent value="upload" className="mt-3">
              <UploadIngestionTab onCapture={handleCapture} />
            </TabsContent>
          </Tabs>

          {draft && (
            <IngestionDraftPreview
              draft={draft}
              onClear={handleClear}
              onUpdate={handleUpdateDraft}
              onRescrape={handleRescrape}
              onSave={handleSave}
              onDelete={handleDelete}
              onExtract={handleExtract}
              isSaving={saveIngestion.isPending}
              isDeleting={deleteIngestion.isPending}
              isExtracting={extractIngestion.isPending}
              savedIngestionId={savedIngestionId}
              reviewStatus={reviewStatus}
              onReviewStatusChange={handleReviewStatusChange}
              structuredExtraction={currentIngestion?.structured_extraction}
              aiNotes={currentIngestion?.ai_notes}
            />
          )}
        </>
      ) : (
        <IngestionHistoryList
          onSelect={handleSelectFromHistory}
          selectedId={savedIngestionId}
        />
      )}
    </div>
  );
};

export default ActivityIngestionPanel;
